import type { CopilotSession } from '@customerio/hydra-copilot';
import type { CopilotAgentModel } from '@customerio/hydra-ui-api';
import { useCopilotModel } from '@customerio/hydra-copilot/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuTriggerButton,
  Strong,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { hydraUiApi } from '@/services/api';
import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import * as styles from '../../index.css';

const MODELS_STALE_TIME = 60 * 60 * 1000;

function useAgentModels(isEnabled: boolean): CopilotAgentModel[] {
  const { account } = useAuth();
  const accountId = account?.id;

  const { data = [] } = useQuery({
    queryKey: ['agent-models', accountId],
    enabled: isEnabled && accountId != null,
    staleTime: MODELS_STALE_TIME,
    // Without this the default 5m collection undoes the staleTime whenever the panel unmounts.
    gcTime: MODELS_STALE_TIME,
    queryFn: async () => {
      const response = await hydraUiApi.copilot.getAgentModels({
        params: { path: { account_id: String(accountId) } },
      });

      return response.models ?? [];
    },
  });

  return data;
}

type ModelPickerProps = {
  session: Pick<CopilotSession, '__model' | '__modelLocked' | 'setModel'>;
};

/**
 * Lets the account choose which model a conversation runs on. Creating the conversation settles
 * the choice, so from then on this is a plain label rather than a menu.
 */
export function ModelPicker({ session }: ModelPickerProps) {
  const canPickModel = useFeatureFlag(AccountFeatureFlag.AgentOpusModel);
  const { model, isLocked } = useCopilotModel(session);
  const models = useAgentModels(canPickModel);

  // The submenu is the whole catalog, featured included, so no model is only in one list.
  const { featured, byProvider, selected } = useMemo(() => {
    const groups = new Map<string, CopilotAgentModel[]>();
    for (const candidate of models) {
      const group = groups.get(candidate.provider);
      if (group != null) {
        group.push(candidate);
      } else {
        groups.set(candidate.provider, [candidate]);
      }
    }

    const chosen = models.find((candidate) => candidate.id === model);

    return {
      featured: models.filter((candidate) => candidate.featured),
      byProvider: [...groups.entries()],
      // Only an exact match, or the catalog's default for a conversation that never chose —
      // falling back to the default for an unknown id would name the wrong model.
      selected: chosen ?? (model === '' ? models.find((candidate) => candidate.default) : undefined),
    };
  }, [models, model]);

  if (!canPickModel) {
    return null;
  }

  if (isLocked) {
    // The catalog may not have loaded, and a conversation can run a model no longer in it, so
    // fall back to the id rather than dropping the label.
    const settled = selected?.name ?? model.split('/').pop();
    if (settled == null || settled === '') {
      return null;
    }

    return (
      <Tooltip
        content={`This conversation runs on ${settled}. Start a new one to use a different model.`}
        placement="top"
        isCustom={true}
      >
        {(context) => (
          <Text
            size="xs"
            color="subtle"
            className={styles.modelLockedLabel}
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          >
            {settled}
          </Text>
        )}
      </Tooltip>
    );
  }

  if (selected == null) {
    return null;
  }

  const modelItem = (candidate: CopilotAgentModel) => (
    <DropdownMenuItem
      key={candidate.id}
      trailingIcon={candidate.id === selected.id ? 'check' : undefined}
      onClick={() => session.setModel(candidate.id)}
    >
      {candidate.name}
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <Tooltip
        content="Pick the model for this conversation. It can't be changed once you send a message."
        placement="top"
        isCustom={true}
        floatingPluginOptions={{ focus: { enabled: false } }}
      >
        {(context) => (
          <DropdownMenuTriggerButton
            variant="tertiary"
            type="button"
            size="sm"
            className={styles.borderlessTrigger}
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          >
            <Strong>{selected.name}</Strong>
          </DropdownMenuTriggerButton>
        )}
      </Tooltip>
      <DropdownMenuContent className={styles.modelMenu}>
        <DropdownMenuGroup label="Model">{featured.map(modelItem)}</DropdownMenuGroup>
        <DropdownMenuGroup>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>View all models</DropdownMenuSubTrigger>
            <DropdownMenuContent className={styles.modelMenu}>
              {byProvider.map(([provider, providerModels]) => (
                <DropdownMenuGroup key={provider} label={provider}>
                  {providerModels.map(modelItem)}
                </DropdownMenuGroup>
              ))}
            </DropdownMenuContent>
          </DropdownMenuSub>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
